/**
 * 추출힌트 자가개선 제안 (M23) React Query 훅.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  approveSuggestion,
  backtestSuggestion,
  fetchFieldErrors,
  generateSuggestions,
  getSuggestionRecordPageImageBlob,
  getSuggestionRecordPagesMeta,
  listSuggestions,
  rejectSuggestion,
  type FieldErrorRow,
  type RuleSuggestionBacktest,
  type RuleSuggestionOut,
  type RuleSuggestionStatus,
} from "@/lib/api/ruleSuggestions";

export const SUGGESTIONS_QUERY_KEY = ["admin", "rule-suggestions"] as const;

export const suggestionsKeys = {
  all: SUGGESTIONS_QUERY_KEY,
  list: (status: RuleSuggestionStatus) => [...SUGGESTIONS_QUERY_KEY, "list", status] as const,
  fieldErrors: (minCount: number, minRate: number) =>
    [...SUGGESTIONS_QUERY_KEY, "field-errors", { minCount, minRate }] as const,
};

export function useFieldErrors(params: { minCount?: number; minRate?: number; enabled?: boolean }) {
  const { minCount = 1, minRate = 0, enabled = true } = params;
  return useQuery<FieldErrorRow[]>({
    queryKey: suggestionsKeys.fieldErrors(minCount, minRate),
    queryFn: () => fetchFieldErrors({ minCount, minRate }),
    enabled,
    staleTime: 30_000,
  });
}

export function useSuggestions(status: RuleSuggestionStatus = "pending", enabled = true) {
  return useQuery<RuleSuggestionOut[]>({
    queryKey: suggestionsKeys.list(status),
    queryFn: () => listSuggestions(status),
    enabled,
    staleTime: 10_000,
  });
}

export function useGenerateSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generateSuggestions(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
    },
  });
}

export interface ApproveSuggestionVars {
  id: number;
  editedHint?: string;
}

export function useApproveSuggestion() {
  const qc = useQueryClient();
  return useMutation<RuleSuggestionOut, Error, ApproveSuggestionVars>({
    mutationFn: ({ id, editedHint }) => approveSuggestion(id, editedHint),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ["admin", "agents"] });
    },
  });
}

export interface BacktestSuggestionVars {
  id: number;
  editedHint?: string;
}

/** 힌트 임시 적용 재추출 검증(온디맨드). 캐시하지 않고 매번 실행. */
export function useBacktestSuggestion() {
  return useMutation<RuleSuggestionBacktest, Error, BacktestSuggestionVars>({
    mutationFn: ({ id, editedHint }) => backtestSuggestion(id, editedHint),
  });
}

export function useRejectSuggestion() {
  const qc = useQueryClient();
  return useMutation<RuleSuggestionOut, Error, number>({
    mutationFn: (id) => rejectSuggestion(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEY });
    },
  });
}

/** 제안 예시(record_id)의 PDF 페이지 수. */
export function useSuggestionRecordMeta(recordId: string | null) {
  return useQuery({
    queryKey: [...SUGGESTIONS_QUERY_KEY, "record-meta", recordId],
    queryFn: () => getSuggestionRecordPagesMeta(recordId as string),
    enabled: !!recordId,
    staleTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });
}

export interface SuggestionRecordImageState {
  url: string | null;
  isLoading: boolean;
  error: Error | null;
}

/** 제안 예시(record_id) 특정 페이지의 PNG → ObjectURL (컴포넌트 lifecycle 관리). */
export function useSuggestionRecordPageImage(
  recordId: string | null,
  page: number,
): SuggestionRecordImageState {
  const query = useQuery<Blob, Error>({
    queryKey: [...SUGGESTIONS_QUERY_KEY, "record-image", recordId, page],
    queryFn: ({ signal }) => getSuggestionRecordPageImageBlob(recordId as string, page, { signal }),
    enabled: !!recordId && page >= 1,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
    retry: 1,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);

  return { url, isLoading: query.isLoading || query.isFetching, error: query.error ?? null };
}
